import * as Updates from "expo-updates"
import { CodedError } from "expo-modules-core"
import {
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity,
  View,
} from "react-native"

import ExpoConstants from "expo-constants"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { router } from "expo-router"

import BackButton from "@/assets/icons/back-arrow-dark.svg"
import React, { type ReactNode } from "react"

import { formatISO9075 } from "date-fns"
import { BFButton, PrimaryButton } from "@/components/buttons/CommonButtons"
import { showActionToast, showErrorToast } from "@/components/toasts/AppToast"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { Body, H2, H5 } from "@/components/standard/BFText"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import Spacer from "@/components/util/Spacer"
import { showDebuggingMarginsStore } from "@/stores/debuggingStore"
import { useStore } from "@nanostores/react"
import DebugIdBox from "@/app/account/DebugIdBox"
import { useAppVersion } from "@/hooks/useAppVersion"

const onBack = () => {
  router.back()
}


const AppMetadataRow = ({
  label,
  value,
}: {
  label: ReactNode
  value: ReactNode
}) => {
  return (
    <View className="flex flex-row items-center justify-start p-4">
      <H5 className="flex-1">{label}</H5>
      <Body className="flex-1 text-left">{value}</Body>
    </View>
  )
}

type UpdateStatus =
  | "update-disabled"
  | "update-current"
  | "update-available"
  | "update-pending"
  | "update-checking"
  | "update-downloading"

const UpdateStatusBadge = ({ status }: { status: UpdateStatus }) => {
  let statusText

  switch (status) {
    case "update-available":
      statusText = "Update available"
      break

    case "update-checking":
      statusText = "Checking..."
      break

    case "update-current":
      statusText = "Latest"
      break

    case "update-disabled":
      statusText = "Disabled"
      break

    case "update-downloading":
      statusText = "Downloading"
      break

    case "update-pending":
      statusText = "Pending restart"
      break
    default:
      statusText = "Disabled"
      break
  }

  return <Body>{statusText}</Body>
}

const AppMetadataCard = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const appVersion = ExpoConstants.expoConfig!.version!
  const { appVersionString, platformTitle } = useAppVersion()

  const runtimeVersion = Updates.runtimeVersion
  const updateChannel = Updates.channel || "N/A"

  const updateEnabled = Updates.isEnabled

  const {
    currentlyRunning,
    isUpdateAvailable,
    isUpdatePending,
    isChecking,
    isDownloading,
  } = Updates.useUpdates()

  let updateStatus: UpdateStatus = "update-current"

  if (!updateEnabled) {
    updateStatus = "update-disabled"
  }

  if (isUpdateAvailable) {
    updateStatus = "update-available"
  }

  if (isUpdatePending) {
    updateStatus = "update-pending"
  }

  if (isChecking) {
    updateStatus = "update-pending"
  }

  if (isDownloading) {
    updateStatus = "update-downloading"
  }

  const currentUpdateCreatedAt = currentlyRunning.createdAt
  const formattedCurrentUpdateCreatedAt = currentUpdateCreatedAt
    ? formatISO9075(currentUpdateCreatedAt)
    : "N/A"

  const { userAgent } = useBrowserInfo()

  return (
    <View className="flex flex-col w-full border rounded border-light-grey">
      <AppMetadataRow label="App Version" value={appVersion} />
      <AppMetadataRow label="Runtime Version" value={runtimeVersion} />
      <AppMetadataRow label={platformTitle} value={appVersionString || "N/A"} />
      <AppMetadataRow label="Channel" value={updateChannel} />
      <AppMetadataRow
        label="Update Status"
        value={<UpdateStatusBadge status={updateStatus} />}
      />
      <AppMetadataRow
        label="Update Created At"
        value={formattedCurrentUpdateCreatedAt}
      />
      <AppMetadataRow label="Browser Info" value={userAgent || "N/A"} />
    </View>
  )
}

export default function DebugScreen() {
  const insets = useSafeAreaInsets()
  const showOverlay = useStore(showDebuggingMarginsStore)

  // Toggle the overlay by updating the persistent store directly
  const toggleOverlay = () => {
    showDebuggingMarginsStore.set(!showOverlay)
  }

  useViewedCallback(() => {
    setStatusBarStyle("dark")
  })

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-white"
      behavior="padding"
      style={{
        paddingTop: insets.top,
      }}
    >
      <View className="flex flex-col px-8 pb-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <H2 className={`text-midnight-blue pt-4`}>Internal Debug</H2>
      </View>
      <ScrollView>
        <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
          <AppMetadataCard />
          <View className="flex flex-col justify-start flex-1 gap-8 pt-8">
            <View>
              <PrimaryButton
                testID={"update-button"}
                title="Check for Updates"
                onPress={() => {
                  const checkForUpdate = async () => {
                    try {
                      const updateCheckResult =
                        await Updates.checkForUpdateAsync()
                      if (updateCheckResult.isAvailable) {
                        const fetchUpdateResult =
                          await Updates.fetchUpdateAsync()
                        if (fetchUpdateResult.isNew) {
                          showActionToast({
                            title: "Update Available",
                            message: "Downloading. Reload when complete.",
                          })
                        }
                      } else {
                        showActionToast({
                          title: "No Update Available",
                          message: "No new updates found.",
                        })
                      }
                    } catch (err: unknown) {
                      if (err instanceof CodedError) {
                        showErrorToast({ message: err.message })
                      }
                    }
                  }
                  void checkForUpdate()
                }}
              />
            </View>
            <View>
              <PrimaryButton
                testID={"ReloadButton"}
                title="Reload"
                onPress={() => {
                  void Updates.reloadAsync().catch((err: unknown) => {
                    if (err instanceof CodedError) {
                      showErrorToast({ message: err.message })
                    }
                  })
                }}
              />
            </View>

            <DebugIdBox />

            <Spacer />
            <View>
              <BFButton
                variant={"secondary"}
                testID={"ReloadButton"}
                title="Toggle Margins"
                onPress={toggleOverlay}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}
